import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, Award, Truck, Shield, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import ProductCard from '../components/ui/ProductCard';
import ProductCardSkeleton from '../components/ui/ProductCardSkeleton';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed database first
        await axios.post(`${API}/seed`);
        
        const [featuredRes, bestsellersRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/products/featured`),
          axios.get(`${API}/products/bestsellers`),
          axios.get(`${API}/categories`)
        ]);

        setFeaturedProducts(featuredRes.data);
        setBestsellers(bestsellersRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter?email=${encodeURIComponent(email)}`);
      toast.success('Successfully subscribed to newsletter!');
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  const testimonials = [
    {
      id: 1,
      name: 'Ahmed Khan',
      location: 'Lahore',
      rating: 5,
      comment: 'Best organic honey I have ever tasted! The quality is exceptional and delivery was quick.',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100'
    },
    {
      id: 2,
      name: 'Fatima Ali',
      location: 'Karachi',
      rating: 5,
      comment: 'Amazing Ajwa dates! Fresh and delicious. Will definitely order again.',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100'
    },
    {
      id: 3,
      name: 'Hassan Malik',
      location: 'Islamabad',
      rating: 5,
      comment: 'Premium quality dry fruits at reasonable prices. Highly recommended!',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100'
    }
  ];

  const benefits = [
    {
      icon: <Leaf className="w-8 h-8" />,
      title: '100% Organic',
      description: 'All products are naturally sourced without chemicals'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Premium Quality',
      description: 'Hand-picked and quality tested for excellence'
    },
    {
      icon: <Truck className="w-8 h-8" />,
      title: 'Fast Delivery',
      description: 'Nationwide delivery within 3-5 business days'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Satisfaction Guaranteed',
      description: 'Not satisfied? Get a full refund'
    }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-[80vh] min-h-[600px] flex items-center" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/764a0469-e026-4f18-9ad9-f58903c92cdd/images/1375f528e545ce965c375eb6c6720fbaab12c0a89c089a78d2453fa0158dd960.png')`
          }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="inline-block px-4 py-2 bg-[#E0A106] text-[#2C1E0F] rounded-full text-sm font-bold uppercase tracking-wider mb-6 animate-fade-in">
              100% Pure & Organic
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight mb-6 animate-slide-up stagger-1" data-testid="hero-title">
              Pure Nature,<br />
              <span className="text-[#E0A106]">Delivered to You</span>
            </h1>
            <p className="text-lg text-white/90 mb-8 max-w-lg animate-slide-up stagger-2" data-testid="hero-subtitle">
              Discover the finest organic honey, premium dates, nutritious seeds, and dry fruits sourced directly from nature.
            </p>
            <div className="flex flex-wrap gap-4 animate-slide-up stagger-3">
              <Link
                to="/shop"
                className="btn-secondary inline-flex items-center gap-2"
                data-testid="hero-shop-now-btn"
              >
                Shop Now
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/shop"
                className="px-6 py-3 rounded-full font-medium border-2 border-white text-white hover:bg-white hover:text-[#1F7A63] transition-all duration-300"
                data-testid="hero-explore-btn"
              >
                Explore Categories
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 bg-white border-y border-[#6B4F2A]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="flex items-center gap-3 text-[#2C1E0F]">
              <div className="w-12 h-12 bg-[#1F7A63]/10 rounded-full flex items-center justify-center">
                <Leaf className="w-6 h-6 text-[#1F7A63]" />
              </div>
              <span className="font-semibold">100% Organic</span>
            </div>
            <div className="flex items-center gap-3 text-[#2C1E0F]">
              <div className="w-12 h-12 bg-[#1F7A63]/10 rounded-full flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#1F7A63]" />
              </div>
              <span className="font-semibold">Lab Tested</span>
            </div>
            <div className="flex items-center gap-3 text-[#2C1E0F]">
              <div className="w-12 h-12 bg-[#1F7A63]/10 rounded-full flex items-center justify-center">
                <Award className="w-6 h-6 text-[#1F7A63]" />
              </div>
              <span className="font-semibold">Premium Quality</span>
            </div>
            <div className="flex items-center gap-3 text-[#2C1E0F]">
              <div className="w-12 h-12 bg-[#1F7A63]/10 rounded-full flex items-center justify-center">
                <Truck className="w-6 h-6 text-[#1F7A63]" />
              </div>
              <span className="font-semibold">COD Available</span>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="section bg-[#FFF8E7]" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#1F7A63] mb-2 block">
              Our Collection
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-[#2C1E0F] tracking-tight">
              Shop by Category
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {categories.map((category, index) => (
              <Link
                key={category.id}
                to={`/shop?category=${category.id}`}
                className="category-card group h-48 md:h-64"
                data-testid={`category-card-${category.id}`}
              >
                <img
                  src={category.image}
                  alt={category.name}
                  className="absolute inset-0 w-full h-full object-cover category-image transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent z-10" />
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 z-20">
                  <h3 className="text-white text-lg md:text-xl font-bold">{category.name}</h3>
                  <p className="text-white/70 text-sm hidden md:block mt-1">{category.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="section" data-testid="featured-products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#1F7A63] mb-2 block">
                Hand Picked
              </span>
              <h2 className="text-3xl md:text-4xl font-black text-[#2C1E0F] tracking-tight">
                Featured Products
              </h2>
            </div>
            <Link
              to="/shop"
              className="hidden md:flex items-center gap-2 text-[#1F7A63] font-medium hover:gap-3 transition-all"
              data-testid="view-all-featured"
            >
              View All
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {loading
              ? [...Array(4)].map((_, i) => <ProductCardSkeleton key={i} />)
              : featuredProducts.slice(0, 4).map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
          </div>

          <div className="mt-8 text-center md:hidden">
            <Link
              to="/shop"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="view-all-featured-mobile"
            >
              View All Products
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section bg-[#1F7A63]" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#E0A106] mb-2 block">
              Why Choose Us
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight">
              The NatureWayz Promise
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 text-center hover:bg-white/20 transition-colors"
                data-testid={`benefit-card-${index}`}
              >
                <div className="w-16 h-16 bg-[#E0A106] rounded-2xl flex items-center justify-center mx-auto mb-4 text-[#2C1E0F]">
                  {benefit.icon}
                </div>
                <h3 className="text-white font-bold text-lg mb-2">{benefit.title}</h3>
                <p className="text-white/70 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bestsellers */}
      <section className="section" data-testid="bestsellers-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#1F7A63] mb-2 block">
                Most Popular
              </span>
              <h2 className="text-3xl md:text-4xl font-black text-[#2C1E0F] tracking-tight">
                Bestsellers
              </h2>
            </div>
            <Link
              to="/shop"
              className="hidden md:flex items-center gap-2 text-[#1F7A63] font-medium hover:gap-3 transition-all"
              data-testid="view-all-bestsellers"
            >
              View All
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {loading
              ? [...Array(4)].map((_, i) => <ProductCardSkeleton key={i} />)
              : bestsellers.slice(0, 4).map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#1F7A63] mb-2 block">
              Testimonials
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-[#2C1E0F] tracking-tight">
              What Our Customers Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="testimonial-card"
                data-testid={`testimonial-${testimonial.id}`}
              >
                <div className="flex items-center gap-0.5 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-[#E0A106] fill-[#E0A106]" />
                  ))}
                </div>
                <p className="text-[#574021] mb-6 leading-relaxed">"{testimonial.comment}"</p>
                <div className="flex items-center gap-4">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-[#2C1E0F]">{testimonial.name}</p>
                    <p className="text-sm text-[#574021]">{testimonial.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section
        className="section relative"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/764a0469-e026-4f18-9ad9-f58903c92cdd/images/09b093db05d5a3d7ab241e48e4ed0af1c8fd9e8acdff4e4096a8d3d742d0c5a0.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="newsletter-section"
      >
        <div className="absolute inset-0 bg-[#1F7A63]/90" />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#E0A106] mb-2 block">
            Stay Updated
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight mb-4">
            Join Our Newsletter
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Subscribe to get exclusive offers, new product updates, and health tips delivered to your inbox.
          </p>
          
          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="flex-1 px-6 py-4 rounded-full bg-white text-[#2C1E0F] placeholder-[#574021]/50 focus:outline-none focus:ring-2 focus:ring-[#E0A106]"
              required
              data-testid="newsletter-email-input"
            />
            <button
              type="submit"
              disabled={subscribing}
              className="btn-secondary px-8 py-4"
              data-testid="newsletter-submit-btn"
            >
              {subscribing ? 'Subscribing...' : 'Subscribe'}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
